import { create } from 'zustand'
import { formatCredits, getBalance, type AgentTurnsStatus, type CreditsSummary } from '@/api/billing'

// Matches the server's seeded ratio, so a surface that renders before the first
// balance read still shows credits rather than nothing.
const DEFAULT_CREDITS_PER_USD = 100

/**
 * The live credit balance shown in the user menu.
 *
 * Refreshed three ways so the number is never stale in a way the user would
 * notice: a cheap visible-only poll, an explicit refresh when the dropdown
 * opens, and an imperative `refreshCredits()` the agent hooks call when a turn
 * finishes (the moment credits actually move).
 */
interface CreditsState {
  balance: number | null
  planCode: string | null
  planLabel: string | null
  /** Drives the lock chip on download entry points, and what it says. */
  allowDownload: boolean
  downloadPlanLabel: string
  scope: CreditsSummary['scope'] | null
  /** Credits per USD, for turning a live turn's provider cost into credits. */
  creditsPerUsd: number
  enabled: boolean
  lowThreshold: number | null
  /** Today's agent turns vs the plan's daily caps; null until loaded. */
  agentTurns: AgentTurnsStatus | null
  loading: boolean
  loaded: boolean
  refresh: () => Promise<void>
  clear: () => void
}

export const useCreditsStore = create<CreditsState>((set, get) => ({
  balance: null,
  planCode: null,
  planLabel: null,
  allowDownload: true,
  downloadPlanLabel: '',
  scope: null,
  creditsPerUsd: DEFAULT_CREDITS_PER_USD,
  enabled: false,
  lowThreshold: null,
  agentTurns: null,
  loading: false,
  loaded: false,

  refresh: async () => {
    if (get().loading) return
    set({ loading: true })
    try {
      const data = await getBalance()
      set({
        balance: data.balance,
        planCode: data.plan_code,
        planLabel: data.plan_label,
        // Default to true so a balance read that fails never puts a false
        // "Enterprise" lock on a user who does have the feature.
        allowDownload: data.allow_runtime_download !== false,
        downloadPlanLabel: data.download_plan_label || '',
        scope: data.scope,
        creditsPerUsd: data.credits_per_usd || DEFAULT_CREDITS_PER_USD,
        enabled: data.credit_billing_enabled,
        lowThreshold: data.low_balance_threshold,
        agentTurns: data.agent_turns ?? null,
        loaded: true,
      })
    } catch {
      // A balance read failing must never surface as an error to the user —
      // the menu simply keeps the last known value (or shows nothing yet).
      set({ loaded: true })
    } finally {
      set({ loading: false })
    }
  },

  clear: () =>
    set({
      balance: null, planCode: null, planLabel: null, allowDownload: true,
      downloadPlanLabel: '', scope: null, creditsPerUsd: DEFAULT_CREDITS_PER_USD,
      enabled: false, lowThreshold: null, agentTurns: null, loaded: false,
    }),
}))

/**
 * Imperative refresh for non-React callers (the agent hooks' terminal handlers,
 * checkout completion, a download that just debited).
 */
export function refreshCredits(): void {
  void useCreditsStore.getState().refresh()
}

/**
 * A provider cost, rendered the only way a user may see it: in credits.
 *
 * Live agent turns report their cost in USD because that is what the provider
 * returns. Every user-facing surface converts here rather than printing the
 * dollar figure — the same rule the usage endpoints enforce server-side by
 * stripping cost entirely.
 */
export function formatSpend(costUsd: number | null | undefined): string | null {
  if (costUsd == null || !Number.isFinite(costUsd) || costUsd <= 0) return null
  const credits = costUsd * useCreditsStore.getState().creditsPerUsd
  return `${formatCredits(credits)} credits`
}
